/**
 * The transcript, as a SCRIPT.
 *
 * What this replaces: a flex-wrapped list of one <TextField> per word, with
 * the speaker and the timestamp hidden in a tooltip. Aziz, on seeing it:
 * "the caption editor looks absolutely fucked… it's just individual words and
 * then timestamps and people… no one will be able to use it."
 *
 * He was right. A transcript is read as dialogue, so it should look like
 * dialogue:
 *
 *     GABE    00:01:23   I genuinely thought we were talking to someone else.
 *     SHAWN   00:01:27   You set this situation up!
 *
 * ── The model ───────────────────────────────────────────────────────────────
 * The WORD ARRAY REMAINS THE SOURCE OF TRUTH. Every word keeps its own
 * start/end because those timings drive caption sync, and a view that threw
 * them away to show prose would silently desync every burned caption.
 *
 * So grouping is a VIEW over the words, never a different document. All four
 * granularities read and write the same array, which is why switching between
 * them cannot lose an edit.
 *
 * Line breaks that the user makes by hand are the one thing a word array
 * cannot express on its own, so a split writes `line_break: true` onto the
 * first word of the new line. `ShortsWord` allows arbitrary keys and the whole
 * object is round-tripped to the renderer, so the break survives a save.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, IconButton, MenuItem, Paper, Select, Slider,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  CallSplit, Merge, RestartAlt, Warning,
} from '@mui/icons-material';
import { ShortsWord } from '../../services/api';

export type Granularity = 'script' | 'onscreen' | 'word' | 'time';

const TIME_BUCKETS = [0.25, 0.5, 1, 2, 5];

/** Roughly how much text fits on one burned caption line. */
const ONSCREEN_MAX_CHARS = 32;
/** A pause this long reads as a new caption card even mid-sentence. */
const ONSCREEN_GAP_S = 0.4;

export const fmtTimecode = (s: number): string => {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = sec.toFixed(2).padStart(5, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/** Accepts `12.5`, `01:12.5`, `1:01:12.5`. Returns null if unparseable. */
export const parseTimecode = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  const parts = t.split(':');
  if (parts.some((p) => p !== '' && isNaN(Number(p)))) return null;
  let s = 0;
  for (const p of parts) s = s * 60 + Number(p || 0);
  return isFinite(s) && s >= 0 ? s : null;
};

export interface Line {
  from: number;          // index of the first word, inclusive
  to: number;            // index of the last word, inclusive
  speaker: string;
  start: number;
  end: number;
  text: string;
}

/**
 * Group words into display lines. Pure, and never mutates `words`.
 *
 * `script` is the default because it is how a human reads dialogue: one line
 * per speaker turn. The other three exist because the right grouping depends
 * on the job — checking caption sync is a different task from fixing a typo.
 */
export function buildLines(
  words: ShortsWord[], mode: Granularity, bucket: number,
): Line[] {
  const lines: Line[] = [];
  if (!words.length) return lines;

  let from = 0;
  const flush = (to: number) => {
    const slice = words.slice(from, to + 1);
    lines.push({
      from,
      to,
      speaker: slice[0]?.speaker || '',
      start: slice[0]?.start ?? 0,
      end: slice[slice.length - 1]?.end ?? 0,
      text: slice.map((w) => w.text).join(' '),
    });
    from = to + 1;
  };

  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const next = words[i + 1];
    if (!next) { flush(i); break; }

    // A hand-made split always wins, in every mode.
    if (next.line_break === true) { flush(i); continue; }
    // A change of speaker always ends a line: merging two people into one
    // line of script would be actively wrong, not merely ugly.
    if ((next.speaker || '') !== (w.speaker || '')) { flush(i); continue; }

    if (mode === 'word') { flush(i); continue; }
    if (mode === 'time') {
      const a = Math.floor((words[from]?.start ?? 0) / bucket);
      const b = Math.floor((next.start ?? 0) / bucket);
      if (b !== a) flush(i);
      continue;
    }
    if (mode === 'onscreen') {
      const sofar = words.slice(from, i + 1).map((x) => x.text).join(' ').length;
      const gap = (next.start ?? 0) - (w.end ?? 0);
      if (sofar + 1 + (next.text?.length ?? 0) > ONSCREEN_MAX_CHARS
          || gap > ONSCREEN_GAP_S) {
        flush(i);
      }
      continue;
    }
    // script: run to the end of the speaker's turn.
  }
  return lines;
}

/**
 * Write an edited line of prose back onto the words it came from.
 *
 * The honest problem: the user edits a sentence, but the renderer needs a
 * time per word. Two cases, and the distinction matters enough to be visible
 * in the UI:
 *
 *  - SAME NUMBER OF WORDS — map one-to-one. Every original timing survives
 *    exactly, which is the overwhelmingly common case (fixing a typo, a
 *    mis-heard word, a swear the transcriber missed).
 *
 *  - WORDS ADDED OR REMOVED — the new words have no timings of their own, so
 *    spread them across the line's existing span, weighted by length. The
 *    line still starts and ends exactly when it did, so the surrounding
 *    captions cannot drift; only the word boundaries inside it are estimated.
 */
export function applyLineText(
  words: ShortsWord[], line: Line, nextText: string,
): { words: ShortsWord[]; retimed: boolean } {
  const tokens = nextText.trim().split(/\s+/).filter(Boolean);
  const original = words.slice(line.from, line.to + 1);

  if (tokens.length === 0) {
    // Deleting every word of a line removes it rather than leaving blanks.
    const out = [...words];
    out.splice(line.from, original.length);
    return { words: out, retimed: false };
  }

  if (tokens.length === original.length) {
    const out = [...words];
    tokens.forEach((t, i) => { out[line.from + i] = { ...original[i], text: t }; });
    return { words: out, retimed: false };
  }

  const start = line.start;
  const span = Math.max(line.end - line.start, 0.001);
  const weights = tokens.map((t) => Math.max(t.length, 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let cursor = start;
  const rebuilt: ShortsWord[] = tokens.map((t, i) => {
    const dur = (weights[i] / total) * span;
    const w: ShortsWord = {
      ...original[0],
      text: t,
      start: cursor,
      end: cursor + dur,
      speaker: line.speaker || original[0]?.speaker,
      retimed: true,
    };
    if (i === 0) {
      if (original[0]?.line_break) w.line_break = true;
      else delete (w as any).line_break;
    } else {
      delete (w as any).line_break;
    }
    cursor += dur;
  return w;
  });
  const out = [...words];
  out.splice(line.from, original.length, ...rebuilt);
  return { words: out, retimed: true };
}

/** Move a whole line in time, keeping its internal cadence intact. */
export function shiftLine(
  words: ShortsWord[], line: Line, newStart: number,
): ShortsWord[] {
  const delta = newStart - line.start;
  if (!delta) return words;
  const out = [...words];
  for (let i = line.from; i <= line.to; i++) {
    out[i] = {
      ...out[i],
      start: Math.max(0, (out[i].start ?? 0) + delta),
      end: Math.max(0, (out[i].end ?? 0) + delta),
    };
  }
  return out;
}

interface Props {
  words: ShortsWord[];
  originalWords?: ShortsWord[];
  disabled?: boolean;
  onChange: (next: ShortsWord[]) => void;
}

const TranscriptScriptEditor: React.FC<Props> = ({
  words, originalWords, disabled, onChange,
}) => {
  const [mode, setMode] = useState<Granularity>('script');
  const [bucketIdx, setBucketIdx] = useState(2);     // 1s
  const [retimedNote, setRetimedNote] = useState(false);

  const bucket = TIME_BUCKETS[bucketIdx];
  const lines = useMemo(
    () => buildLines(words, mode, bucket), [words, mode, bucket]);

  const speakers = useMemo(() => {
    const s = new Set<string>();
    words.forEach((w) => { if (w.speaker) s.add(w.speaker); });
    return Array.from(s).sort();
  }, [words]);

  const changedAt = useCallback((i: number) => {
    const o = originalWords?.[i];
    return o?.text !== undefined && o.text !== words[i]?.text;
  }, [originalWords, words]);

  const setText = useCallback((line: Line, text: string) => {
    const { words: next, retimed } = applyLineText(words, line, text);
    if (retimed) setRetimedNote(true);
    onChange(next);
  }, [words, onChange]);

  const setSpeaker = useCallback((line: Line, name: string, everywhere: boolean) => {
    const was = line.speaker;
    const next = words.map((w, i) => {
      if (everywhere) return (w.speaker || '') === was ? { ...w, speaker: name } : w;
      return i >= line.from && i <= line.to ? { ...w, speaker: name } : w;
    });
    onChange(next);
  }, [words, onChange]);

  const setStart = useCallback((line: Line, raw: string) => {
    const t = parseTimecode(raw);
    if (t === null) return;
    onChange(shiftLine(words, line, t));
  }, [words, onChange]);

  /** Break a line in two at a word boundary. */
  const splitAt = useCallback((wordIdx: number) => {
    if (wordIdx <= 0 || wordIdx >= words.length) return;
    const next = [...words];
    next[wordIdx] = { ...next[wordIdx], line_break: true };
    onChange(next);
  }, [words, onChange]);

  /** Undo a hand-made break, rejoining this line with the one above. */
  const mergeUp = useCallback((line: Line) => {
    const next = [...words];
    const w = { ...next[line.from] };
    delete (w as any).line_break;
    next[line.from] = w;
    onChange(next);
  }, [words, onChange]);

  if (!words.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No word-level transcript came with this clip.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center"
             sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <ToggleButtonGroup
          size="small" exclusive value={mode}
          onChange={(_, v) => v && setMode(v)}
        >
          <ToggleButton value="script">Script</ToggleButton>
          <ToggleButton value="onscreen">On screen</ToggleButton>
          <ToggleButton value="word">Per word</ToggleButton>
          <ToggleButton value="time">By time</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'time' && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary">
              {bucket}s
            </Typography>
            <Slider
              size="small" min={0} max={TIME_BUCKETS.length - 1} step={1}
              marks value={bucketIdx}
              onChange={(_, v) => setBucketIdx(v as number)}
              valueLabelDisplay="off"
              sx={{ flex: 1 }}
            />
          </Stack>
        )}

        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {lines.length} line{lines.length === 1 ? '' : 's'} · {words.length} words
        </Typography>
      </Stack>

      {mode === 'onscreen' && (
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
          Approximate: grouped at ~{ONSCREEN_MAX_CHARS} characters or a{' '}
          {ONSCREEN_GAP_S}s pause. The renderer does the real chunking, so treat
          this as a close preview rather than the exact cards.
        </Typography>
      )}

      {retimedNote && (
        <Alert severity="info" icon={<Warning />} sx={{ mb: 1.5 }}
               onClose={() => setRetimedNote(false)}>
          You changed how many words a line has, so the new words were spread
          across that line's existing span. The line still starts and ends when
          it did — only the word boundaries inside it are estimated.
        </Alert>
      )}

      <Stack spacing={0.5}>
        {lines.map((line) => {
          const isSplit = words[line.from]?.line_break === true;
          const dirty = Array.from(
            { length: line.to - line.from + 1 }, (_, k) => line.from + k,
          ).some(changedAt);
          return (
            <Paper key={`${line.from}-${line.to}`} variant="outlined"
                   sx={{ p: 1, bgcolor: dirty ? 'action.hover' : undefined }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {speakers.length > 0 ? (
                  <Select
                    size="small" variant="standard" disableUnderline
                    disabled={disabled}
                    value={speakers.includes(line.speaker) ? line.speaker : ''}
                    displayEmpty
                    onChange={(e) => setSpeaker(line, String(e.target.value), false)}
                    sx={{
                      width: 96, flexShrink: 0,
                      '& .MuiSelect-select': {
                        fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
                        textTransform: 'uppercase', py: 0.5,
                      },
                    }}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {speakers.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Box sx={{ width: 96, flexShrink: 0 }} />
                )}

                <Tooltip title="Moves the whole line, keeping its internal timing">
                  <TextField
                    size="small" variant="standard" disabled={disabled}
                    defaultValue={fmtTimecode(line.start)}
                    key={`t-${line.from}-${line.start}`}
                    onBlur={(e) => setStart(line, e.target.value)}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      width: 78, flexShrink: 0,
                      '& input': {
                        fontFamily: 'monospace', fontSize: 12, py: 0.5,
                        color: 'text.secondary',
                      },
                    }}
                  />
                </Tooltip>

                <TextField
                  size="small" variant="standard" fullWidth multiline
                  disabled={disabled}
                  defaultValue={line.text}
                  key={`x-${line.from}-${line.to}-${line.text.length}`}
                  onBlur={(e) => {
                    if (e.target.value !== line.text) setText(line, e.target.value);
                  }}
                  InputProps={{ disableUnderline: true }}
                  sx={{ '& textarea': { fontSize: 14, lineHeight: 1.5 } }}
                />

                <Stack direction="row" sx={{ flexShrink: 0 }}>
                  <Tooltip title="Start a new line here">
                    <span>
                      <IconButton size="small" disabled={disabled || line.to <= line.from}
                                  onClick={() => splitAt(line.to)}>
                        <CallSplit fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={isSplit ? 'Rejoin with the line above'
                                          : 'Nothing to rejoin — not a hand-made break'}>
                    <span>
                      <IconButton size="small" disabled={disabled || !isSplit}
                                  onClick={() => mergeUp(line)}>
                        <Merge fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              {mode !== 'word' && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, pl: '104px' }}>
                  {dirty && <Chip size="small" color="primary" variant="outlined"
                                  label="edited" sx={{ height: 18, fontSize: 10 }} />}
                  {words.slice(line.from, line.to + 1).some((w) => w.retimed) && (
                    <Chip size="small" color="warning" variant="outlined"
                          label="re-timed" sx={{ height: 18, fontSize: 10 }} />
                  )}
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>

      {speakers.length > 1 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Renaming a speaker here changes only that line.
          </Typography>
          <Button
            size="small" startIcon={<RestartAlt />} disabled={disabled}
            onClick={() => {
              const from = window.prompt('Rename which speaker?', speakers[0]);
              if (!from) return;
              const to = window.prompt(`Rename "${from}" to:`, from);
              if (!to || to === from) return;
              onChange(words.map((w) =>
                (w.speaker || '') === from ? { ...w, speaker: to } : w));
            }}
          >
            Rename a speaker everywhere
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default TranscriptScriptEditor;
